import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import BackButton from "../components/BackButton";
import LoadingOverlay from "../components/LoadingOverlay";
import AnnouncementForm from "../components/announcements/AnnouncementForm";
import {
  createAnnouncement,
  getAnnouncementDetail,
  getAnnouncementUserOptions,
  updateAnnouncement,
} from "../services/announcementService";
import {
  createEmptyAnnouncementForm,
  DEFAULT_ROLE_OPTIONS,
  getUserOptionLabel,
  KNOWN_ANNOUNCEMENT_PAGES,
  mapAnnouncementToFormValues,
  buildAnnouncementPayload,
  validateAnnouncementForm,
} from "../utils/announcementUtils";

const AnnouncementFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isCreateMode = location.pathname.endsWith("/create");
  const isEditMode = location.pathname.endsWith("/edit");
  const isViewMode = Boolean(id) && !isEditMode;

  const [formValues, setFormValues] = useState(createEmptyAnnouncementForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState(DEFAULT_ROLE_OPTIONS);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const pageOptions = useMemo(() => KNOWN_ANNOUNCEMENT_PAGES, []);

  const fetchSupportingData = useCallback(async () => {
    try {
      const users = await getAnnouncementUserOptions();
      const userList = Array.isArray(users) ? users : [];

      setUserOptions(
        userList.map((user) => ({
          value: user._id,
          label: getUserOptionLabel(user),
        }))
      );

      const uniqueDepartments = Array.from(
        new Set(
          userList
            .map((user) => String(user.department || "").trim())
            .filter(Boolean)
        )
      ).map((value) => ({
        value,
        label: value,
      }));

      setRoleOptions(DEFAULT_ROLE_OPTIONS);
      setDepartmentOptions(uniqueDepartments);
    } catch (error) {
      console.error("Fetch announcement support data error:", error);
      toast.error("โหลดข้อมูลประกอบฟอร์มประกาศไม่สำเร็จ");
    }
  }, []);

  const fetchDetail = useCallback(async () => {
    if (!id) return;

    setLoading(true);

    try {
      const response = await getAnnouncementDetail(id);
      const announcement = response?.data || response;
      setFormValues(mapAnnouncementToFormValues(announcement));
    } catch (error) {
      console.error("Fetch announcement detail error:", error);
      toast.error(error?.response?.data?.message || "โหลดรายละเอียดประกาศไม่สำเร็จ");
      navigate("/announcement-management", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchSupportingData();
  }, [fetchSupportingData]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationMessage = validateAnnouncementForm(formValues);
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    try {
      setSaving(true);
      const payload = buildAnnouncementPayload(formValues);

      if (id && isEditMode) {
        await updateAnnouncement(id, payload);
        toast.success("อัปเดตประกาศเรียบร้อย");
      } else if (isCreateMode) {
        await createAnnouncement(payload);
        toast.success("สร้างประกาศเรียบร้อย");
      }

      navigate("/announcement-management");
    } catch (error) {
      console.error("Save announcement error:", error);
      toast.error(error?.response?.data?.message || "บันทึกประกาศไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = isCreateMode
    ? "Create Announcement"
    : isEditMode
    ? "Edit Announcement"
    : "Announcement Detail";
  const pageDescription = isViewMode
    ? "ดูรายละเอียดประกาศและเงื่อนไขการแสดงผล"
    : "กำหนดการแสดง popup ตามกลุ่มผู้ใช้ หน้าที่ต้องการ และช่วงเวลา";

  return (
    <div className="relative min-h-screen bg-gray-50 px-4 py-6 transition-colors duration-300 dark:bg-gray-900 sm:px-6">
      <LoadingOverlay loading={loading || saving} />

      <div className="mb-8">
        <div className="mb-3 flex items-center justify-between">
          <BackButton label="ย้อนกลับ" />
        </div>

        <div>
          <h1 className="text-[26px] font-semibold text-gray-900 dark:text-gray-100">
            {pageTitle}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {pageDescription}
          </p>
        </div>
      </div>

      <AnnouncementForm
        formValues={formValues}
        onChange={handleChange}
        onSubmit={handleSubmit}
        saving={saving}
        readOnly={isViewMode}
        roleOptions={roleOptions}
        departmentOptions={departmentOptions}
        userOptions={userOptions}
        pageOptions={pageOptions}
      />
    </div>
  );
};

export default AnnouncementFormPage;
